import React from 'react'
import { Link } from 'react-router-dom'
import { useSelectedOrg } from '@/context/SelectedOrgContext'

// ── Tasks ───────────────────────────────────────────────────────────────────────
// Each task is a job-to-be-done that deep-links into an existing Integrations
// guide (or, for "Invite your team", org settings). Mirrors the GUIDES array
// pattern in pages/Integrations.tsx.

type Task = {
  icon: string
  title: string
  blurb: string
  to: string
  cta: string
}

export function GettingStarted() {
  const { selectedOrg } = useSelectedOrg()
  const slug = selectedOrg?.slug ?? null
  const canInvite = !!selectedOrg && ['owner', 'admin'].includes(selectedOrg.role)

  const tasks: Task[] = [
    // Owner/admin only: links to a settings tab non-admins can't act on. First
    // because the rest of the list is faster to work through with the team in.
    ...(canInvite && slug
      ? [{
          icon: '⊕',
          title: 'Invite your team',
          blurb: 'Bring teammates into this organisation so they can publish, consume, and review alongside you.',
          to: `/org/${slug}/settings?tab=members`,
          cta: 'Manage members',
        } as Task]
      : []),
    {
      icon: '❯',
      title: 'Install the CLI',
      blurb: 'One binary that signs you in, publishes modules, pushes lock files and drives state – on your laptop and in CI, with no stored secret.',
      to: '/integrations/cli-getting-started',
      cta: 'CLI guide',
    },
    {
      icon: '⬆',
      title: 'Publish a module',
      blurb: 'Package and publish your first Terraform or OpenTofu module. Terramantle stores, scans, and serves every version.',
      to: '/integrations/module-publishing',
      cta: 'Publishing guide',
    },
    {
      icon: '◳',
      title: 'Use providers',
      blurb: 'Serve scanned upstream and terramantle-built providers from your org registry with a standard source address.',
      to: '/integrations/using-providers',
      cta: 'Providers guide',
    },
    {
      icon: '▤',
      title: 'Store state',
      blurb: 'Use Terramantle as a native HTTP backend – state is versioned, scanned, and locked automatically.',
      to: '/integrations/storing-state',
      cta: 'State guide',
    },
    {
      icon: '⚷',
      title: 'Trust your pipeline',
      blurb: 'Authenticate GitHub Actions and GitLab CI with their native OIDC tokens – no stored client secrets.',
      to: '/integrations/oidc-trust',
      cta: 'Pipeline Trust guide',
    },
  ]

  return (
    <div className="fu">
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.kicker}>// getting started</div>
        <h2 style={s.title}>Get up and running</h2>
        <p style={s.intro}>
          Pick a starting point below. Each one walks you through the setup end to end – from
          your first module to wiring Terramantle into CI.
        </p>
      </div>

      {/* ── Why this page ── */}
      <div style={s.whyPanel}>
        <div style={s.whyKicker}>// why you&rsquo;re seeing this</div>
        <h3 style={s.whyTitle}>Not enough data for analytics yet</h3>
        <p style={s.whyPara}>
          Your dashboard normally shows download trends, scan findings, and your highest-risk
          modules and workspaces. It needs a baseline of activity to work from first – a few
          published versions, some provider pulls, or a state push or two – before those charts
          have anything meaningful to plot.
        </p>
        <p style={{ ...s.whyPara, marginBottom: 0 }}>
          As soon as Terramantle records your first module download, served provider, or state
          push, this launchpad is replaced by your live analytics automatically. The tasks below are
          the fastest way to get there.
        </p>
      </div>

      {/* ── Task grid ── */}
      <div style={s.grid}>
        {tasks.map((t) => (
          <Link
            key={t.title}
            to={t.to}
            style={s.card}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg3)' }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg2)' }}
          >
            <div style={s.cardIcon}>{t.icon}</div>
            <div style={s.cardTitle}>{t.title}</div>
            <p style={s.cardBlurb}>{t.blurb}</p>
            <div style={s.cardCta}>{t.cta} →</div>
          </Link>
        ))}
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Inline-style idiom + design tokens, matching OnboardingBanner / Dashboard.

const s: Record<string, React.CSSProperties> = {
  header: {
    marginBottom: 20,
  },
  kicker: {
    fontFamily: 'var(--mono)',
    fontSize: '0.65rem',
    color: 'var(--text3)',
    letterSpacing: '0.08em',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: 'var(--head)',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: 8,
    marginTop: 0,
  },
  intro: {
    fontFamily: 'var(--body)',
    fontSize: '0.95rem',
    color: 'var(--text2)',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: 640,
  },
  whyPanel: {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 22px',
    marginBottom: 24,
  },
  whyKicker: {
    fontFamily: 'var(--mono)',
    fontSize: '0.6rem',
    color: 'var(--text3)',
    letterSpacing: '0.08em',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  whyTitle: {
    fontFamily: 'var(--head)',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'var(--text)',
    marginTop: 0,
    marginBottom: 8,
  },
  whyPara: {
    fontFamily: 'var(--body)',
    fontSize: '0.85rem',
    color: 'var(--text2)',
    lineHeight: 1.65,
    marginTop: 0,
    marginBottom: 10,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 16,
  },
  card: {
    display: 'block',
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
    textDecoration: 'none',
    transition: 'border-color 0.15s, background 0.15s',
  },
  cardIcon: {
    fontFamily: 'var(--head)',
    fontSize: '1.4rem',
    color: 'var(--accent)',
    marginBottom: 10,
    lineHeight: 1,
  },
  cardTitle: {
    fontFamily: 'var(--head)',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 6,
  },
  cardBlurb: {
    fontFamily: 'var(--body)',
    fontSize: '0.82rem',
    color: 'var(--text2)',
    lineHeight: 1.55,
    margin: '0 0 14px 0',
  },
  cardCta: {
    fontFamily: 'var(--mono)',
    fontSize: '0.72rem',
    color: 'var(--accent)',
    letterSpacing: '0.02em',
  },
}
